import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import api, { isSession } from '../../services/api';
import { errorMessage, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, Empty, ErrorBox, Field, Modal, PageHeader, SecretBox, Spinner, Table, Td, ago, dateTime, inputClass } from '../../components/ui';

interface Key {
  id: string;
  name: string;
  prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export default function KeysPage() {
  const session = isSession();
  const { data, error, loading, reload } = useFetch(async () => (session ? (await api.get<{ keys: Key[] }>('/account/keys')).data.keys : []));
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  if (!session) {
    return (
      <div>
        <PageHeader title="Clés d'API" subtitle="Les clés qui identifient vos applications" />
        <Card>
          <Empty>
            Vous êtes connecté avec une clé d&apos;API. Pour gérer vos clés, déconnectez-vous puis connectez-vous avec votre adresse e-mail (« Recevoir un code »).
          </Empty>
        </Card>
      </div>
    );
  }

  async function revoke(key: Key) {
    if (!confirm(`Révoquer la clé « ${key.name} » (${key.prefix}…) ? Toute application qui l'utilise cessera de fonctionner.`)) return;
    setFailure(null);
    try {
      await api.delete(`/account/keys/${key.id}`);
      await reload();
    } catch (e) {
      setFailure(errorMessage(e));
    }
  }

  const active = (data ?? []).filter((k) => !k.revoked_at);
  const revoked = (data ?? []).filter((k) => k.revoked_at);

  return (
    <div>
      <PageHeader
        title="Clés d'API"
        subtitle="Une clé par application ou par environnement. Elle n'est montrée qu'à sa création."
        actions={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" />Nouvelle clé</Button>}
      />
      {failure && <div className="mb-4"><ErrorBox message={failure} /></div>}

      <Card>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox message={error} onRetry={reload} />
        ) : active.length === 0 ? (
          <Empty>Aucune clé active. Créez-en une pour appeler l&apos;API.</Empty>
        ) : (
          <Table head={['Nom', 'Début de la clé', 'Créée', 'Dernière utilisation', '']}>
            {active.map((key) => (
              <tr key={key.id}>
                <Td className="font-medium text-slate-900">{key.name}</Td>
                <Td><code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{key.prefix}…</code></Td>
                <Td className="whitespace-nowrap">{dateTime(key.created_at)}</Td>
                <Td>{ago(key.last_used_at)}</Td>
                <Td className="text-right"><Button variant="danger" small onClick={() => revoke(key)}>Révoquer</Button></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <p className="mt-4 text-sm text-slate-500">
        Pas de clé sous la main ? Vous ne pouvez pas la retrouver : seule son empreinte est conservée. Créez-en une nouvelle et révoquez l&apos;ancienne.
        Besoin d&apos;un exemple d&apos;appel ? Voir <Link to="/integration" className="text-indigo-600 hover:underline">Intégration</Link>.
      </p>

      {revoked.length > 0 && (
        <Card title="Clés révoquées" className="mt-6">
          <ul className="divide-y divide-slate-100 text-sm">
            {revoked.map((key) => (
              <li key={key.id} className="flex items-center justify-between py-2 text-slate-500">
                <span>{key.name} <code className="text-xs">{key.prefix}…</code></span>
                <Badge>Révoquée le {dateTime(key.revoked_at)}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(key) => { setCreated(key); setCreating(false); reload(); }} />}
      {created && (
        <Modal title="Votre nouvelle clé" onClose={() => setCreated(null)}>
          <SecretBox label="Clé d'API" value={created} />
          <div className="mt-4 flex justify-end"><Button onClick={() => setCreated(null)}>J&apos;ai copié la clé</Button></div>
        </Modal>
      )}
    </div>
  );
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string) => void }) {
  const [name, setName] = useState('Production');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      onCreated((await api.post<{ key: string }>('/account/keys', { name })).data.key);
    } catch (e) {
      setError(errorMessage(e));
      setLoading(false);
    }
  }

  return (
    <Modal title="Nouvelle clé d'API" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nom" hint="Par exemple : Production, Site web, Test">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required autoFocus />
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={loading} disabled={!name.trim()}>Créer la clé</Button>
        </div>
      </form>
    </Modal>
  );
}
